/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { Refresh as RefreshIcon } from "@mui/icons-material";

import moment from "moment";
import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { LoadingButton } from "@mui/lab";
import {
  getPaymentReports,
} from "app/redux/slice/vendorSlice";
import Loader from "@jumbo/components/Loader";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const validationSchema = yup.object().shape({
  toDate: yup.date().when("fromDate", (fromDate, schema) => {
    if (fromDate) {
      return schema
        .required("To Date is required")
        .min(fromDate, "To Date cannot be before From Date");
    }
    return schema;
  }),
});

const PaymentReport = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState({});
  const [dateRange, setDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(false);

  const getData = async (values, formikHelpers = {}) => {
    setLoading(true);
    const { setSubmitting } = formikHelpers;

    if (setSubmitting) setSubmitting(true);
    const value = {
      fromDate: values?.fromDate,
      toDate: values?.toDate,
      type: values?.type,
    };

    const response = await dispatch(getPaymentReports(value)).unwrap();
    if (response?.status === 200) {
      setData(response?.data?.data);
      if (setSubmitting) setSubmitting(false);
      setLoading(false);
    } else {
      enqueueSnackbar(response?.data?.response_message, { variant: "error" });
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      fromDate: "",
      toDate: "",
      type: "outStanding",
    },
    validateOnChange: true,
    validationSchema: validationSchema,
    onSubmit: getData,
  });
  const { values, handleSubmit, isSubmitting, setFieldValue } = formik;

  useEffect(() => {
    getData({ type: values?.type }, {});
  }, []);

  const totalReceivables = data?.receivables?.reduce(
    (a, b) => a + b?.totalAmount,
    0
  );
  const totalPayables = data?.payables?.reduce((a, b) => a + b?.totalAmount, 0);

  const tableStyle = {
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid #e0e0e0",
  };

  return (
    <>
      <Card sx={{ display: "flex", mb: 3 }}>
        <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
          <CardContent>
            <FormikProvider value={formik}>
              <Form noValidate autoComplete="off">
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateRangePicker
                        startText="From Date - To Date"
                        endText="To Date"
                        inputFormat="dd/MM/yyyy"
                        value={dateRange}
                        onChange={(newValue) => {
                          setDateRange(newValue);
                          setFieldValue(
                            "fromDate",
                            moment(newValue[0]).format("YYYY-MM-DD")
                          );
                          setFieldValue(
                            "toDate",
                            moment(newValue[1]).format("YYYY-MM-DD")
                          );
                        }}
                        renderInput={(
                          { inputProps, ...startProps },
                          endProps
                        ) => {
                          const startValue = inputProps.value;
                          delete inputProps.value;
                          return (
                            <TextField
                              {...startProps}
                              inputProps={inputProps}
                              value={`${startValue || "DD/MM/YYYY"} - ${
                                endProps.inputProps.value || "DD/MM/YYYY"
                              }`}
                              fullWidth
                              size="small"
                            />
                          );
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={3.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ButtonGroup size="small" variant="outlined">
                        {QUICK_RANGES?.map((r) => (
                          <Button
                            key={r.key}
                            onClick={() => {
                              setDateRange(r.getRange());
                              setFieldValue("fromDate", moment(r.getRange()[0]).format("YYYY-MM-DD"));
                              setFieldValue("toDate", moment(r.getRange()[1]).format("YYYY-MM-DD"));
                            }}
                            disabled={loading}
                          >
                            {r.label}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={values?.type}
                        onChange={(e) => {
                          setFieldValue("type", e.target.value);
                          getData({ ...values, type: e.target.value }, {});
                        }}
                        label="Type"
                        size="small"
                      >
                        <MenuItem value={"outStanding"}>Outstanding</MenuItem>
                        <MenuItem value={"payReceive"}>Pay & Receive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={1.75}>
                    <LoadingButton
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleSubmit();
                      }}
                      loading={isSubmitting}
                      loadingIndicator={<CircularProgress size={20} />}
                    >
                      Submit
                    </LoadingButton>
                  </Grid>
                  <Grid item xs={12} md={1.75}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        getData({ type: values?.type }, {});
                        setFieldValue("fromDate", "");
                        setFieldValue("toDate", "");
                        setDateRange([null, null]);
                      }}
                      startIcon={<RefreshIcon />}
                      fullWidth
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            </FormikProvider>
          </CardContent>
        </Div>
      </Card>

      {loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <>
              <Typography
                variant="h5"
                align="center"
                fontSize={20}
                fontWeight={600}
                sx={{ mt: 2, mb: 0 }}
              >
                {values?.type === "outStanding"
                  ? "Outstanding Report"
                  : "Pay & Receive Report"}
              </Typography>

              <Box p={3}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: "#e8f5e9" }}>
                      <CardContent>
                        <Typography variant="h6" color="green">
                          Total{" "}
                          {values?.type === "outStanding"
                            ? "Receivables"
                            : "Received"}
                        </Typography>
                        <Typography variant="h4">{totalReceivables}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: "#ffebee" }}>
                      <CardContent>
                        <Typography variant="h6" color="red">
                          Total{" "}
                          {values?.type === "outStanding" ? "Payables" : "Paid"}
                        </Typography>
                        <Typography variant="h4">{totalPayables}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: "#e3f2fd" }}>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          Net Balance
                        </Typography>
                        <Typography variant="h4">
                          {totalReceivables - totalPayables}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography
                          variant="h6"
                          mb={2}
                          fontWeight={600}
                          color="green"
                        >
                          {values?.type === "outStanding"
                            ? "Receivables"
                            : "Received"}
                        </Typography>

                        <Table sx={tableStyle}>
                          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                            <TableRow>
                              <TableCell>
                                <strong>Vendor</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Amount</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data?.receivables?.length !== 0 ? (
                              data?.receivables?.map((row) => (
                                <TableRow key={row?._id}>
                                  <TableCell>{row?.vendorName}</TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="h6"
                                      mb={0}
                                      color="green"
                                    >
                                      {row?.totalAmount}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell align="center" colSpan={2}>
                                  No Data Found
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography
                          variant="h6"
                          mb={2}
                          fontWeight={600}
                          color="red"
                        >
                          {values?.type === "outStanding" ? "Payables" : "Paid"}
                        </Typography>

                        <Table sx={tableStyle}>
                          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                            <TableRow>
                              <TableCell>
                                <strong>Vendor</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Amount</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data?.payables?.length !== 0 ? (
                              data?.payables?.map((row) => (
                                <TableRow key={row?._id}>
                                  <TableCell>{row?.vendorName}</TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="h6"
                                      mb={0}
                                      color="error"
                                    >
                                      {row?.totalAmount}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell align="center" colSpan={2}>
                                  No Data Found
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default PaymentReport;
